<template>
  <base-section
    id="product-intro"
    space="50"
  >
    <base-section-heading
      color="#142d7a"
      :title="$t('products.intro')"
      :subtitle="$t('products.subtitle')"
      :mobile-size="'text-h5'"
      :size="'text-h4'"
      :space="0"
    />

    <v-container class="pa-0 custom-container">
      <v-row
        v-if="loading"
        class="ma-0"
      >
        <v-col
          cols="12"
          class="text-center py-12"
        >
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
          />
          <p class="mt-4">
            {{ $t('common.loading') }}
          </p>
        </v-col>
      </v-row>
      <v-row
        v-else
        class="ma-0"
      >
        <v-col
          cols="12"
          class="pa-0"
        >
          <v-row class="ma-0">
            <v-col
              v-for="(image, index) in productImages"
              :key="`${image}-${index}`"
              cols="12"
              class="pa-0"
            >
              <img
                :src="image"
                class="product-image"
                alt="Product detail image"
                @error="handleImageError(image, index)"
                @load="handleImageLoad(image, index)"
              >
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
  </base-section>
</template>

<script>
  import productsData from '@/data/products.json'
  import { loadImages } from '@/utils/imageLoader'

  export default {
    name: 'ProductIntro',

    data: () => ({
      productImages: [],
      loading: false,
    }),

    computed: {
      isMobile() {
        return this.$vuetify.breakpoint.width <= 768
      },
    },

    watch: {
      '$route.params.itemId': {
        handler(newId) {
          this.loadProductImages(newId)
        },
        immediate: true,
      },
      '$i18n.locale': {
        handler() {
          if (this.$route.params.itemId) {
            this.loadProductImages(this.$route.params.itemId)
          }
        },
      },
    },

    methods: {
      // 获取当前语言代码（zh 或 en）
      getCurrentLocale() {
        return this.$i18n.locale === 'zh' ? 'zh' : 'en'
      },

      // 从路径中提取相对路径（移除 @/assets/ 前缀）
      extractRelativePath(fullPath) {
        if (fullPath.startsWith('@/assets/')) {
          return fullPath.replace('@/assets/', '')
        }
        return fullPath
      },

      async loadProductImages(itemId) {
        if (!itemId) {
          this.productImages = []
          return
        }

        this.loading = true
        try {
          // 从 products.json 中找到对应的产品
          const product = productsData.products.find(p => p.id === Number(itemId))

          if (!product) {
            console.warn(`Product with id ${itemId} not found`)
            this.productImages = []
            return
          }

          // 根据当前语言选择对应的图片数组
          const imagePaths = this.getCurrentLocale() === 'zh'
            ? product.detailImages || []
            : product.detailImagesEn || []

          if (imagePaths.length === 0) {
            console.warn(`No detail images found for product ${itemId}`)
            this.productImages = []
            return
          }

          // 提取相对路径并加载图片
          const relativePaths = imagePaths.map(path => this.extractRelativePath(path))
          const loadedImages = await loadImages(relativePaths)

          // 更严格的过滤：确保是有效的字符串且不为空
          const validImages = loadedImages.filter(img => {
            const isValid = img &&
              typeof img === 'string' &&
              img.trim() !== '' &&
              !img.includes('undefined') &&
              !img.includes('null')
            if (!isValid) {
              console.warn('Filtered invalid image:', img)
            }
            return isValid
          })

          // 直接使用有效图片，原生 img 标签可以处理轻微损坏的图片
          // 不需要预验证，避免增加加载时间
          this.productImages = validImages
          console.log(`Loaded ${this.productImages.length} valid images out of ${imagePaths.length} total for product ${itemId}`)
        } catch (error) {
          console.error('加载产品图片失败:', error)
          this.productImages = []
        } finally {
          this.loading = false
        }
      },

      // 验证图片是否可以加载
      validateImage(imageUrl) {
        return new Promise((resolve, reject) => {
          const img = new Image()
          img.onload = () => resolve(true)
          img.onerror = () => reject(new Error(`Failed to load image: ${imageUrl}`))
          img.src = imageUrl
          // 设置超时，避免长时间等待
          setTimeout(() => {
            if (!img.complete) {
              reject(new Error(`Image load timeout: ${imageUrl}`))
            }
          }, 5000)
        })
      },

      handleImageError(imageUrl, index) {
        console.warn(`Image failed to load at index ${index}:`, imageUrl)
        // 不立即移除，因为原生 img 标签比 v-img 更宽松，可能只是解码警告
        // 只有在真正无法显示时才移除
      },

      handleImageLoad(imageUrl, index) {
        // 图片成功加载，不做处理
        // console.log(`Image loaded successfully at index ${index}:`, imageUrl)
      },
    },
  }
</script>

<style lang="sass" scoped>
.custom-container
  max-width: none !important

::v-deep .base-section-heading
  margin-bottom: 0 !important
  .base-divider
    margin-bottom: 8px !important

.product-image
  width: 100%
  height: auto
  display: block
  object-fit: contain
</style>
